import { useState, useEffect, useCallback } from "react"
import { useHistory, Route, Switch } from "react-router-dom"
import {
	useLogin,
	useLogo,
	setUpSignatureLogin,
	navigationAllowedOnSignature,
	tearDownSignatureLogin,
	FeaturesProvider,
	ConfigProvider,
	retrieveSignature,
	storeSignature,
	createSecurityParameters,
	refreshAraAccessToken,
	setCurrentTokenType,
	deleteRefreshCode,
	araTheme,
	AppContextProvider,
	useMountEffect,
	NavigationContextProvider,
	TopLevel,
	evoMigrationRedirect,
	lazyWithForcedRefresh,
	useMouseWithinWindowTracking,
} from "shared"
import { AppShellPublic } from "./layouts/AppShellPublic"
import { useConfirmationDialog } from "shared/src/components/shadcn/components/molecules"
import { BreadcrumbProvider } from "shared/src/contexts"
import { useQuery } from "shared/src/libs/queryLib/queryLib"
import type { User, Company, JWT, SignatureInfo } from "shared"
import { Auth } from "@aws-amplify/auth"
import { onError } from "shared/src/libs/errorLib/errorLib"
import { config } from "./config"
import SinglePageRoutes, { useRefreshToken } from "./SinglePageRoutes"
import { MantineProvider } from "@mantine/core"
import { TanstackQueryProvider } from "shared/src/components/providers/TanstackQueryProvider"
import { Toaster } from "sonner"
import { TooltipProvider } from "shared/src/components/shadcn/components/ui/tooltip"

const Routes = lazyWithForcedRefresh(() => import(/* webpackChunkName: "Routes" */ "./Routes"))

const nullCompany: Company = { name: null, id: null }

function AppMain() {
	useRefreshToken()
	const { showAlertInfo, DialogComponent } = useConfirmationDialog()
	const query = useQuery()
	const newSig = query.get("sig") as JWT
	let sig
	let forceSignature = false

	// Adapter to match the old DialogBox API signature
	const showAlert = useCallback(
		async (
			message: string | React.ReactNode,
			title: string | React.ReactNode,
			confirmButtonText?: string
		): Promise<boolean> => {
			await showAlertInfo(message, typeof title === "string" ? title : "Alert", confirmButtonText)
			return true
		},
		[showAlertInfo]
	)
	if (newSig != null && newSig.length > 0) {
		storeSignature(newSig) // always overwrite with new signature
		sig = newSig
		forceSignature = true
	} else {
		sig = retrieveSignature() as JWT
	}
	const redirect = query.get("redirect")
	const signatureInRedirect = redirect?.includes("sig=")
	const signatureProvided = sig != null
	const [user, setUser] = useState<User>({ isAuthenticated: signatureProvided, name: "" } as User)
	const [company, setCompany] = useState<Company>(nullCompany)
	const performLogin = useLogin({
		responseOnly: true,
		setCompanyOverride: setCompany,
		setUserOverride: setUser,
		showAlert,
	})
	const history = useHistory()
	const { logo, setLogoAndSendToDb, fetchLogoFromDb, sendLogoToDb } = useLogo()
	const [isAuthenticating, setIsAuthenticating] = useState(true)
	const [navigationAllowed, setNavigationAllowed] = useState(sig?.length == null)
	const [signatureInfo, setSignatureInfo] = useState<SignatureInfo>({
		hasSignature: signatureProvided,
		expired: false,
		signatureValid: true, // assume valid until proven otherwise
	})
	const [accessIdToken, setAccessIdToken] = useState<string | undefined>(undefined)

	const origin = window.location.origin.toString()

	useEffect(() => {
		// add listener to check if user login has expired when tab is brought back into focus
		const checkLogin = async () => {
			if (user.isAuthenticated) {
				await createSecurityParameters({}) // this will perform logout and reload if user login has expired
			}
		}
		document.addEventListener("visibilitychange", checkLogin)
		return () => {
			document.removeEventListener("visibilitychange", checkLogin)
		}
	}, [user?.isAuthenticated, signatureProvided])

	async function cognitoLogin() {
		try {
			setCurrentTokenType("cognito")
			const response = await Auth.currentUserInfo()
			// I think either Auth.currentUserInfo() or Auth.currentAuthenticatedUser({bypassCache: true}) is ok to use here
			// Both seem to verify the user is logged in, but Auth.currentUserInfo()
			// returns only the user info, not the jwts, which is fine for our purposes
			// If changing the error needs to be 'No current user' or 'The user is not authenticated' respectively
			if (response !== null && response !== undefined && response.username !== undefined) {
				await performLogin({ response })
				// if the cognito response includes an access id token we can load the button
				setAccessIdToken(response?.attributes?.["custom:access_id_token"])
				setNavigationAllowed(true)
				return true
			}
		} catch (e) {
			if (e !== "No current user" && e !== "The user is not authenticated") {
				onError(e)
			}
		}
		return false
	}

	async function tokenBasedLogin() {
		try {
			setCurrentTokenType("signature")
			console.log("signature provided")
			// use signature based security
			const constructedResponse = setUpSignatureLogin(sig)
			if (constructedResponse != null) {
				await performLogin({ response: constructedResponse })
				setNavigationAllowed(navigationAllowedOnSignature(sig))
			} else {
				setSignatureInfo((sInfo) => ({ ...sInfo, signatureExpired: true }))
				setUser({ ...user, isAuthenticated: false })
			}
			return true
		} catch (e) {
			if (e?.response?.status === 401 || e?.response?.status === 403) {
				console.log("signature invalid")
				// set inauthenticated and invalid to correctly display access denied page
				setSignatureInfo((sInfo) => ({ ...sInfo, signatureValid: false }))
				setUser({ ...user, isAuthenticated: false })
				return false
			}
		}
		return false
	}

	async function onLoad() {
		if (signatureInRedirect) {
			return // don't try to login if a signature is in redirect
		}
		//force signature login if signature is provided in query
		if (forceSignature) {
			const loggedInWithToken = await tokenBasedLogin()
			if (loggedInWithToken) {
				console.log("logged in with signature")
				return
			}
		}
		// use user based security
		const loggedInWithCognito = await cognitoLogin()
		if (loggedInWithCognito) {
			console.log("logged in with cognito")
			return
		}
		// if signature in local storage and no cognito login, try to login with signature
		if (signatureProvided && !forceSignature) {
			const loggedInWithToken = await tokenBasedLogin()
			if (loggedInWithToken) {
				console.log("logged in with stored signature")
				return
			}
		}
		// check for refresh token and run refresh sequence if found
		try {
			const { accessToken } = await refreshAraAccessToken()
			sig = accessToken
			storeSignature(accessToken)
			const loggedInWithToken = await tokenBasedLogin()
			if (loggedInWithToken) {
				return
			}
		} catch (e) {
			console.log("token refresh failed", e)
		}
		console.log("no signature provided")
		// arriving at this point means logins didn't error, but neither was successful
		// should do nothing to allow redirect behaviour to take over (?)
	}

	useMountEffect(async () => {
		await onLoad()
		setIsAuthenticating(false)
	})

	useEffect(() => {
		if (logo == null) {
			fetchLogoFromDb(company)
		}
	}, [company])

	async function handleLogout() {
		await Auth.signOut()
		setUser({ isAuthenticated: false, name: "" } as User)
		setCompany(nullCompany)
		tearDownSignatureLogin()
		deleteRefreshCode()
		history.push("/login")
	}

	function logoLink() {
		return user.isAuthenticated ? config.homepage : "/"
	}

	useEffect(() => {
		if (user.isAuthenticated && company.evoEnabled && !user.evoEnabled && !isAuthenticating) {
			evoMigrationRedirect({
				companyId: company.id,
				userId: user.id,
				ssoEnabled: accessIdToken != null,
				showAlert,
			})
		}
	}, [accessIdToken, company, isAuthenticating, showAlert, user])

	return (
		<>
			{DialogComponent}
			<FeaturesProvider company={company} user={user}>
				<NavigationContextProvider
					navigationAllowed={navigationAllowed}
					setNavigationAllowed={setNavigationAllowed}
					signatureInfo={signatureInfo}>
					<Switch>
						<Route
							path={
								origin.endsWith("candidates.live")
									? undefined
									: [
											"/singlepageview",
											"/bullhorn",
											"/bullhornredirect",
											"/accessDenied",
											"/candidate",
											"/accessexpired",
											"/entrypoint",
											"/oauthcallback",
											"/apply",
											"/cookies",
										] // candidates.live shouldn't be able to load the main site
							}>
							<TopLevel isAuthenticating={isAuthenticating}>
								<AppContextProvider
									value={{ user, setUser, company, setCompany }}
									logoValue={{ logo, setLogoAndSendToDb, fetchLogoFromDb, sendLogoToDb }}>
									<SinglePageRoutes mapHomeToCandidatePage={origin.endsWith("candidates.live")} />
								</AppContextProvider>
							</TopLevel>
						</Route>
						<Route>
							<BreadcrumbProvider>
								<AppShellPublic
									clientId={company.id}
									evoEnabled={company.evoEnabled}
									accessIdToken={user.evoEnabled ? accessIdToken : undefined}
									company={company}
									user={user}
									userName={user.name}
									onLogout={handleLogout}
									logo={logo}
									showNavbar={user.isAuthenticated}
									isAuthenticating={isAuthenticating}>
									<AppContextProvider
										value={{ user, setUser, company, setCompany }}
										logoValue={{ logo, setLogoAndSendToDb, fetchLogoFromDb, sendLogoToDb }}>
										<Routes />
									</AppContextProvider>
								</AppShellPublic>
							</BreadcrumbProvider>
						</Route>
					</Switch>
				</NavigationContextProvider>
			</FeaturesProvider>
		</>
	)
}

function BaseProviders({ children }: Readonly<{ children: React.ReactNode }>) {
	// Initialize mouse tracking for blur detection in edit boxes
	useMouseWithinWindowTracking()

	// wrap App in all necessary providers that don't require the initial loading logic
	return (
		<TanstackQueryProvider>
			<ConfigProvider config={config}>
				<TooltipProvider delayDuration={250}>
					<MantineProvider theme={araTheme}>{children}</MantineProvider>
				</TooltipProvider>
			</ConfigProvider>
			<Toaster />
		</TanstackQueryProvider>
	)
}

function App() {
	return (
		<BaseProviders>
			<AppMain />
		</BaseProviders>
	)
}

export default App
